import * as Dialog from "@radix-ui/react-dialog";
import { useMemo, useState } from "react";
import { Box, Circle, CircleDot, Combine, Cone, Grid3X3, Minus, Move3D, Search, Sparkles, Triangle, X } from "lucide-react";
import { useModelStore } from "../state/store";
import { sendViewportCommand } from "../viewport/commands";

type Command = { label: string; group: string; keywords: string; icon: React.JSX.Element; action: () => void };

export function CommandPalette(): React.JSX.Element {
  const open = useModelStore((state) => state.commandPaletteOpen);
  const setOpen = useModelStore((state) => state.setCommandPaletteOpen);
  const [query, setQuery] = useState("");
  const store = useModelStore();

  const commands = useMemo<Command[]>(() => [
    { label: "Create box", group: "Create", keywords: "cube primitive", icon: <Box size={16} />, action: () => store.addPrimitive("box") },
    { label: "Create cylinder", group: "Create", keywords: "round primitive", icon: <CircleDot size={16} />, action: () => store.addPrimitive("cylinder") },
    { label: "Create sphere", group: "Create", keywords: "ball primitive", icon: <Circle size={16} />, action: () => store.addPrimitive("sphere") },
    { label: "Create cone", group: "Create", keywords: "frustum primitive", icon: <Cone size={16} />, action: () => store.addPrimitive("cone") },
    { label: "Create wedge", group: "Create", keywords: "ramp primitive", icon: <Triangle size={16} />, action: () => store.addPrimitive("wedge") },
    { label: "Add hole", group: "Modify", keywords: "cut drill", icon: <CircleDot size={16} />, action: () => store.addOperation("hole") },
    { label: "Add fillet", group: "Modify", keywords: "round edge", icon: <Sparkles size={16} />, action: () => store.addOperation("fillet") },
    { label: "Add transform", group: "Modify", keywords: "move rotate scale", icon: <Move3D size={16} />, action: () => store.addOperation("transform") },
    { label: "Linear pattern", group: "Modify", keywords: "array duplicate", icon: <Grid3X3 size={16} />, action: () => store.addOperation("linearPattern") },
    { label: "Union selected", group: "Boolean", keywords: "combine fuse", icon: <Combine size={16} />, action: () => store.addBoolean("union") },
    { label: "Subtract selected", group: "Boolean", keywords: "difference cut", icon: <Minus size={16} />, action: () => store.addBoolean("subtract") },
    { label: "Fit model to view", group: "View", keywords: "zoom frame", icon: <Search size={16} />, action: () => sendViewportCommand("fit") },
    { label: "Isometric view", group: "View", keywords: "camera iso", icon: <Box size={16} />, action: () => sendViewportCommand("iso") }
  ], [store]);

  const filtered = commands.filter((command) => `${command.label} ${command.group} ${command.keywords}`.toLowerCase().includes(query.toLowerCase().trim()));
  const run = (command: Command): void => { command.action(); setOpen(false); setQuery(""); };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay command-overlay" />
        <Dialog.Content className="command-dialog">
          <div className="command-search"><Search size={18} /><input autoFocus placeholder="Search commands…" value={query} onChange={(event) => setQuery(event.target.value)} /><Dialog.Close asChild><button className="icon-button"><X size={16} /></button></Dialog.Close></div>
          <div className="command-results">
            {filtered.length === 0 ? <div className="command-empty">No matching commands</div> : filtered.map((command, index) => (
              <button key={`${command.group}-${command.label}`} className="command-row" onClick={() => run(command)} autoFocus={index === 0 && Boolean(query)}>
                <span>{command.icon}</span><strong>{command.label}</strong><small>{command.group}</small>
              </button>
            ))}
          </div>
          <div className="command-footer"><span>↑↓ Navigate</span><span>Enter Run</span><span>Esc Close</span></div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
